import { useMutation, useReadQuery, type QueryRef } from '@apollo/client/react';
import { toast } from 'sonner';

import { Skeleton } from '@/components/ui/skeleton';
import { BillingPayment, BillingCard } from 'src/components/Billing';
import { CompanyDocument } from 'src/graphql/actions/company/company.query';
import { UpdatePaymentMethodDocument } from 'src/graphql/actions/profile/updatePaymentMethod.mutation';
import { withSuspense } from 'src/hoc';
import { Button } from '@/components/ui/button';

import type { IBillingQuery } from 'src/graphql/actions/billing/billing.query';

type Props = {
  queryRef: QueryRef<IBillingQuery>;
};

const BillingPaymentContainer = ({ queryRef }: Props) => {
  const [updatePaymentMethod] = useMutation(UpdatePaymentMethodDocument, {
    refetchQueries: [CompanyDocument],
  });
  const {
    data: { billing },
  } = useReadQuery(queryRef);

  const paymentMethod = billing?.paymentMethod;

  const updatePayment = async () => {
    try {
      const { data } = await updatePaymentMethod();

      if (data?.updatePaymentMethod?.success) {
        window.location.href = data.updatePaymentMethod.url || '';
      } else {
        throw new Error(data?.updatePaymentMethod?.errors?.[0]);
      }
    } catch (error: unknown) {
      if (error instanceof Error) toast.error(error.message);
    }
  };

  return (
    <BillingCard
      title="Payment Method"
      description="View and update your payment method here."
      className="mb-4"
      action={
        billing?.policy.update && (
          <Button onClick={updatePayment}>{paymentMethod?.type === 'card' ? 'Update' : 'Add Card'}</Button>
        )
      }
    >
      <BillingPayment paymentMethod={paymentMethod} />
    </BillingCard>
  );
};

const PaymentSkeleton = () => (
  <BillingCard title="Payment Method" description="View and update your payment method here." className="mb-4">
    <Skeleton className="h-4 w-full" />
  </BillingCard>
);

export default withSuspense(BillingPaymentContainer, PaymentSkeleton);
